import { CdkDrag, CdkDragDrop, CdkDropList, moveItemInArray
       } from '@angular/cdk/drag-drop';
import { Component, ElementRef, Inject, OnDestroy } from '@angular/core';

import { DesignStepService
       } from 'collage-designer/design-step/design-step.service';
import { PlaceholderType
       } from 'collage-designer/stage/placeholder/placeholder';
import { PlaceholderService
       } from 'collage-designer/stage/placeholder/placeholder.service';
import { TextPlaceholder
       } from 'collage-designer/stage/placeholder/text-placeholder';
import { ProductService } from 'core/product/product.service';
import { EventTrackingService } from 'core/tracking/event-tracking.service';
import { Subscription } from 'rxjs';
import { ToolboxComponent } from '../toolbox.component';
import { ToolboxService } from '../toolbox.service';

@Component({
    selector: 'text-edit-toolbox',
    templateUrl: './text-edit-toolbox.component.html',
    styleUrls: [
        './text-edit-toolbox.component.scss', '../toolbox.component.scss'],
    standalone: false
})
export class TextEditToolboxComponent extends ToolboxComponent
    implements OnDestroy
{
  private static readonly ICONS: Array<string> = [
      'empty-text', 'text-add', 'text-edit', 'text-delete'];

  protected name: string = 'TextEditToolbox';

  private subscriptions: Subscription = new Subscription();
  private stackedPlaceholders: Array<TextPlaceholder> = [];

  constructor(
      private elRef: ElementRef,
      @Inject(ToolboxService) toolboxService,
      @Inject(ProductService) private productService: ProductService,
      @Inject(PlaceholderService) private placeholderService:
          PlaceholderService,
      @Inject(DesignStepService) private designStepService: DesignStepService,
      @Inject(EventTrackingService) private eventTrackingService:
          EventTrackingService)
  {
    super(toolboxService);
    this.subscriptions.add(
        this.toolboxService.targetChanged.subscribe(
            () => this.respondToToolboxChange()));
  }

  public ngOnDestroy(): void
  {
    this.subscriptions.unsubscribe();
  }

  public get isCreateNewButtonVisible(): boolean
  {
    return this.designStepService.getDesignStepConfig()
      .textPlaceholdersSettings.showCreateNewButton;
  }

  public createNewTextPlaceholder(): void
  {
    this.eventTrackingService.track({
      category: this.productService.getCurrentProduct().id,
      action: 'enter_text',
      label: 'start'
    });
    this.toolboxService.createNewPlaceholder(PlaceholderType.Text);
  }

  public get placeholders(): Array<TextPlaceholder>
  {
    const current = this.placeholderService.getTextPlaceholders();
    const cached = this.stackedPlaceholders;

    if (current.length !== cached.length ||
        current.some(p => !cached.includes(p))) {
      this.stackedPlaceholders = current.sort(
          (a, b) => TextEditToolboxComponent.compareStacking(b, a));
    }
    return this.stackedPlaceholders;
  }

  public respondToPlaceholderDrop(
      drop: CdkDragDrop<Array<TextPlaceholder>>): void
  {
    if (drop.previousIndex === drop.currentIndex) {
      return;
    }

    const placeholders = drop.container.data;
    const movedUp = drop.currentIndex < drop.previousIndex;
    placeholders[drop.previousIndex].restack(
        placeholders[drop.currentIndex], movedUp ? 'above' : 'below');

    const restacked = [...placeholders];
    moveItemInArray(restacked, drop.previousIndex, drop.currentIndex);
    this.stackedPlaceholders = restacked;
  }

  public hasSameParent(
      index: number,
      drag: CdkDrag<TextPlaceholder>,
      dropList: CdkDropList<Array<TextPlaceholder>>): boolean
  {
    return drag.data.group.node.parentNode
        === dropList.data[index]?.group.node.parentNode;
  }

  private static compareStacking(
      a: TextPlaceholder, b: TextPlaceholder): number
  {
    const position = a.group.node.compareDocumentPosition(b.group.node);
    if (position & Node.DOCUMENT_POSITION_FOLLOWING) {
      return -1;
    }
    if (position & Node.DOCUMENT_POSITION_PRECEDING) {
      return 1;
    }
    return 0;
  }

  private respondToToolboxChange(): void
  {
    const soleTextPlaceholder =
        this.placeholderService.getSoleTextPlaceholder();
    if (!soleTextPlaceholder ||
        this.toolboxService.getActiveToolboxName('design-step') !== this.name ||
        this.toolboxService.getActiveToolboxName('placeholder')
            === 'TextToolbox') {
      return;
    }

    if (this.placeholderService.getFocusedPlaceholder()
        === soleTextPlaceholder) {
      this.btnCloseClick();
    }
    else {
      soleTextPlaceholder.clickEvent();
    }
  }
}
